import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { canAdmin } from '../utils/permissions';
import { WALKTHROUGH_TOTAL_STEPS } from '../features/onboarding/walkthroughConstants';
import { useWelcomeWalkthrough } from '../hooks/useWelcomeWalkthrough';
import { trackTelemetryEvent } from '../utils/telemetry';
import { dashboardPanelHref } from '../constants/dashboardPanels';
const WelcomeWalkthroughContext = createContext(null);
export function WelcomeWalkthroughProvider({ children }) {
    const { user } = useAuth();
    const userId = user?.id ?? '';
    const role = (user?.role ?? 'viewer');
    const walkthroughTotalSteps = canAdmin(role) ? WALKTHROUGH_TOTAL_STEPS : WALKTHROUGH_TOTAL_STEPS - 1;
    const walkthrough = useWelcomeWalkthrough(userId, { totalSteps: walkthroughTotalSteps });
    const [walkthroughOpen, setWalkthroughOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    /** Avoids duplicate URL-replay handling under React StrictMode (effects run twice on mount). */
    const urlReplayHandledKeyRef = useRef(null);
    const requestOpenWalkthrough = useCallback((options) => {
        if (!user) {
            console.info('[walkthrough] requestOpenWalkthrough ignored: no signed-in user', {
                source: options.source,
            });
            return;
        }
        if (walkthroughOpen) {
            console.info('[walkthrough] requestOpenWalkthrough ignored: tour already open', {
                source: options.source,
            });
            return;
        }
        if (options.source === 'url_replay') {
            walkthrough.replay();
        }
        else if (walkthrough.status === 'complete' ||
            walkthrough.status === 'skipped' ||
            walkthrough.status === 'not_started') {
            walkthrough.replay();
        }
        setWalkthroughOpen(true);
        trackTelemetryEvent('walkthrough_started', {
            userId: user.id,
            source: options.source,
        });
    }, [user, walkthroughOpen, walkthrough.replay, walkthrough.status]);
    const selectStep = useCallback((stepIndex) => {
        walkthrough.goToStep(stepIndex);
        if (stepIndex <= 3) {
            navigate('/');
            return;
        }
        if (stepIndex === 4) {
            navigate(dashboardPanelHref('library'));
            return;
        }
        if (stepIndex === 5) {
            navigate(dashboardPanelHref('reviews'));
            return;
        }
        if (stepIndex === 6 && canAdmin((user?.role ?? 'viewer'))) {
            navigate('/templates');
        }
    }, [navigate, walkthrough, user?.role]);
    const onSkip = useCallback(() => {
        walkthrough.skip();
        setWalkthroughOpen(false);
        if (user) {
            trackTelemetryEvent('walkthrough_skipped', { userId: user.id });
        }
    }, [walkthrough, user]);
    const onComplete = useCallback(() => {
        walkthrough.complete();
        if (user) {
            trackTelemetryEvent('walkthrough_completed', { userId: user.id });
        }
    }, [walkthrough, user]);
    const onClose = useCallback(() => {
        setWalkthroughOpen(false);
    }, []);
    const value = useMemo(() => ({
        walkthrough,
        walkthroughOpen,
        setWalkthroughOpen,
        requestOpenWalkthrough,
        selectStep,
        onSkip,
        onComplete,
        onClose,
    }), [walkthrough, walkthroughOpen, requestOpenWalkthrough, selectStep, onSkip, onComplete, onClose]);
    // Help menu / header: /?walkthrough=replay
    useEffect(() => {
        if (!user)
            return;
        const params = new URLSearchParams(location.search);
        if (params.get('walkthrough') !== 'replay') {
            urlReplayHandledKeyRef.current = null;
            return;
        }
        const dedupeKey = `${location.pathname}${location.search}`;
        if (urlReplayHandledKeyRef.current === dedupeKey) {
            return;
        }
        urlReplayHandledKeyRef.current = dedupeKey;
        requestOpenWalkthrough({ source: 'url_replay' });
        navigate({ pathname: '/', search: '' }, { replace: true });
    }, [location.pathname, location.search, navigate, requestOpenWalkthrough, user]);
    return (_jsx(WelcomeWalkthroughContext.Provider, { value: value, children: children }));
}
export function useWelcomeWalkthroughContext() {
    const ctx = useContext(WelcomeWalkthroughContext);
    if (!ctx) {
        throw new Error('useWelcomeWalkthroughContext must be used within WelcomeWalkthroughProvider');
    }
    return ctx;
}
